package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable
import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.roundToLong

/*
 * SH-131: owner-level (shared) costs. The accountant, software, the owner's car or the company's
 * insurance belong to the business as a whole. A shared cost (owners/{uid}/shared_costs/{id}) has
 * a total, a currency and a date (one-off) or a frequency, and an allocation across the owner's
 * workplaces. Each workplace's part becomes a cost of its month (rosters/{id}/costs/shared_<id>,
 * sourceType ALLOCATED_FROM_OWNER), so the workplaces' results and roster_financials count it once.
 *
 * The server (functions/sharedCosts.js) validates, splits and writes the parts; this file is the
 * same arithmetic for the editor's live preview and for the domain tests. Keep them in step.
 */

/** How often a shared cost is charged. One-off costs have a date, the others a starting month. */
enum class SharedCostFrequency { ONE_OFF, MONTHLY, YEARLY }

/** How a shared cost is split across workplaces. */
@Serializable
enum class AllocationMethod {
    /** Each workplace's percentage; they add up to 100%. */
    PERCENTAGES,

    /** Each workplace's amount; they add up to the total. */
    FIXED_AMOUNTS,

    /** By each workplace's share of the month's [AllocationBasis]. */
    PROPORTIONAL
}

/** What a [AllocationMethod.PROPORTIONAL] split follows, measured per workplace for the month. */
@Serializable
enum class AllocationBasis {
    NET_SALES,
    /** The owner's income from the workplace (commission or sales, fixed revenue, other revenue). */
    INCOME,
    LABOUR_COST,
    /** The same part for every workplace in the split. */
    EQUAL
}

data class SharedCost(
    val id: String,
    val name: String,
    /** The whole amount, net: per month for MONTHLY, per year for YEARLY. */
    val amount: Double,
    val currency: String,
    val frequency: SharedCostFrequency,
    /** ONE_OFF: the day it is charged (ISO). */
    val date: String? = null,
    /** MONTHLY / YEARLY: the first month charged ("YYYY-MM"). */
    val startMonth: String? = null,
    /** MONTHLY / YEARLY: the last month charged ("YYYY-MM"), none = open-ended. */
    val endMonth: String? = null,
    val method: AllocationMethod,
    /** PROPORTIONAL only. */
    val basis: AllocationBasis? = null,
    /** The workplaces in the split, in the owner's order. */
    val workplaceIds: List<String>,
    /** PERCENTAGES: percent per workplace; FIXED_AMOUNTS: amount per workplace; else unused. */
    val shares: Map<String, Double> = emptyMap()
) {
    /** What this cost charges in [year]/[month]; null when the month is not one of its months. */
    fun amountIn(year: Int, month: Int): Double? {
        val key = year * 100 + month
        return when (frequency) {
            SharedCostFrequency.ONE_OFF -> {
                val d = date ?: return null
                if (monthKey(d) == key) amount else null
            }
            else -> {
                val start = startMonth?.let(::monthKey) ?: return null
                val end = endMonth?.let(::monthKey)
                if (key < start || (end != null && key > end)) return null
                if (frequency == SharedCostFrequency.YEARLY) SharedCostAllocation.round2(amount / 12) else amount
            }
        }
    }

    /** The day the parts are dated in [year]/[month]: the one-off's date, else the 1st. */
    fun dateIn(year: Int, month: Int): String =
        if (frequency == SharedCostFrequency.ONE_OFF && date != null) date
        else "$year-${month.toString().padStart(2, '0')}-01"

    private fun monthKey(text: String): Int? {
        val year = text.take(4).toIntOrNull() ?: return null
        val month = text.drop(5).take(2).toIntOrNull() ?: return null
        return year * 100 + month
    }
}

/** One workplace's part of a shared cost in a month. [sharePercent] is of the month's total. */
data class AllocatedPart(val workplaceId: String, val amount: Double, val sharePercent: Double)

/** A month's split; [fellBackToEqual] when the basis was zero everywhere (e.g. no sales yet). */
data class Allocation(val parts: List<AllocatedPart>, val fellBackToEqual: Boolean = false) {
    val total: Double get() = SharedCostAllocation.round2(parts.sumOf { it.amount })
}

/** Where a cost of a month came from when it is a workplace's part of a shared cost. */
@Serializable
data class CostAllocation(
    val sharedCostId: String,
    val method: AllocationMethod,
    val basis: AllocationBasis? = null,
    /** This part's share of the shared cost's month total, in percent. */
    val sharePercent: Double,
    /** The shared cost's total in the month. */
    val total: Double
)

/** A shared cost that cannot be saved: what is wrong, for the editor to say. */
class InvalidSharedCostException(val problems: Set<SharedCostAllocation.Problem>) :
    Exception("Invalid shared cost: $problems")

object SharedCostAllocation {

    enum class Problem {
        NAME_MISSING,
        AMOUNT_NOT_POSITIVE,
        DATE_MISSING,
        START_MONTH_MISSING,
        END_BEFORE_START,
        NO_WORKPLACES,
        NEGATIVE_SHARE,
        /** PERCENTAGES that do not add up to 100. */
        PERCENTAGES_NOT_100,
        /** FIXED_AMOUNTS that do not add up to the total. */
        AMOUNTS_NOT_TOTAL,
        BASIS_MISSING
    }

    fun toCents(amount: Double): Long = (amount * 100).roundToLong()
    fun round2(amount: Double): Double = toCents(amount) / 100.0

    /**
     * Splits [total] by [weights] in whole grosze. Each part is rounded down; the grosze left over
     * go to the largest share (the first in [weights]' order on a tie), so the parts always add up
     * to the total exactly. Weights of zero get nothing; all zero (or none) splits equally.
     */
    fun split(total: Double, weights: List<Pair<String, Double>>): List<AllocatedPart> {
        if (weights.isEmpty()) return emptyList()
        val totalCents = toCents(total)
        val positive = weights.map { (id, w) -> id to if (w.isFinite() && w > 0) w else 0.0 }
        val sum = positive.sumOf { it.second }
        val used = if (sum > 0) positive else positive.map { it.first to 1.0 }
        val usedSum = used.sumOf { it.second }
        val cents = used.map { (id, w) -> id to floor(totalCents * w / usedSum + 1e-6).toLong() }.toMutableList()
        val remainder = totalCents - cents.sumOf { it.second }
        if (remainder != 0L) {
            val largest = used.indices.maxWith(compareBy<Int> { used[it].second }.thenByDescending { it })
            cents[largest] = cents[largest].first to cents[largest].second + remainder
        }
        return cents.map { (id, c) ->
            AllocatedPart(id, c / 100.0, if (totalCents == 0L) 0.0 else round2(c * 100.0 / totalCents))
        }
    }

    /**
     * The split of [cost]'s [monthTotal] in a month. [participants] are the workplaces with a month
     * (roster) to put a part in; [basis] each one's figure for a PROPORTIONAL split.
     *
     * PERCENTAGES and FIXED_AMOUNTS give each workplace its own part whether or not the others
     * have a month yet (theirs waits for their roster). PROPORTIONAL splits among [participants]
     * only, so a workplace whose month is created later (mid-month) joins the next recalculation.
     */
    fun allocate(
        cost: SharedCost,
        monthTotal: Double,
        participants: Collection<String> = cost.workplaceIds,
        basis: Map<String, Double> = emptyMap()
    ): Allocation = when (cost.method) {
        AllocationMethod.PERCENTAGES -> {
            val parts = split(monthTotal, cost.workplaceIds.map { it to (cost.shares[it] ?: 0.0) })
            Allocation(parts.map { it.copy(sharePercent = round2(cost.shares[it.workplaceId] ?: 0.0)) })
        }
        AllocationMethod.FIXED_AMOUNTS -> {
            // The amounts are the parts. A yearly cost's month is a twelfth of each.
            val factor = if (cost.amount > 0) monthTotal / cost.amount else 0.0
            val weights = cost.workplaceIds.map { it to (cost.shares[it] ?: 0.0) * factor }
            Allocation(if (factor == 1.0) exactParts(monthTotal, weights) else split(monthTotal, weights))
        }
        AllocationMethod.PROPORTIONAL -> {
            val inSplit = cost.workplaceIds.filter { it in participants }
            val weights = inSplit.map { id ->
                id to if (cost.basis == AllocationBasis.EQUAL) 1.0 else (basis[id] ?: 0.0).coerceAtLeast(0.0)
            }
            val fellBack = cost.basis != AllocationBasis.EQUAL && weights.isNotEmpty() && weights.all { it.second <= 0.0 }
            Allocation(split(monthTotal, weights), fellBack)
        }
    }

    private fun exactParts(total: Double, amounts: List<Pair<String, Double>>): List<AllocatedPart> {
        val totalCents = toCents(total)
        return amounts.map { (id, a) ->
            val c = toCents(a)
            AllocatedPart(id, c / 100.0, if (totalCents == 0L) 0.0 else round2(c * 100.0 / totalCents))
        }
    }

    /** What stops [cost] from being saved; empty when it may be. */
    fun problems(cost: SharedCost): Set<Problem> = buildSet {
        if (cost.name.isBlank()) add(Problem.NAME_MISSING)
        if (!(cost.amount > 0.0) || !cost.amount.isFinite()) add(Problem.AMOUNT_NOT_POSITIVE)
        when (cost.frequency) {
            SharedCostFrequency.ONE_OFF -> if (cost.date.isNullOrBlank()) add(Problem.DATE_MISSING)
            else -> {
                if (cost.startMonth.isNullOrBlank()) add(Problem.START_MONTH_MISSING)
                else if (cost.endMonth != null && cost.endMonth < cost.startMonth) add(Problem.END_BEFORE_START)
            }
        }
        if (cost.workplaceIds.isEmpty()) add(Problem.NO_WORKPLACES)
        val values = cost.workplaceIds.map { cost.shares[it] ?: 0.0 }
        when (cost.method) {
            AllocationMethod.PERCENTAGES -> {
                if (values.any { it < 0 }) add(Problem.NEGATIVE_SHARE)
                if (toCents(values.sum()) != 10_000L) add(Problem.PERCENTAGES_NOT_100)
            }
            AllocationMethod.FIXED_AMOUNTS -> {
                if (values.any { it < 0 }) add(Problem.NEGATIVE_SHARE)
                if (values.sumOf { toCents(it) } != toCents(cost.amount)) add(Problem.AMOUNTS_NOT_TOTAL)
            }
            AllocationMethod.PROPORTIONAL -> if (cost.basis == null) add(Problem.BASIS_MISSING)
        }
    }

    /**
     * How far the typed shares are from adding up: percentage points missing (PERCENTAGES) or
     * the amount missing (FIXED_AMOUNTS); negative when over. 0 when they add up or for PROPORTIONAL.
     */
    fun shortfall(cost: SharedCost): Double {
        val values = cost.workplaceIds.sumOf { toCents(cost.shares[it] ?: 0.0) }
        return when (cost.method) {
            AllocationMethod.PERCENTAGES -> (10_000L - values) / 100.0
            AllocationMethod.FIXED_AMOUNTS -> (toCents(cost.amount) - values) / 100.0
            AllocationMethod.PROPORTIONAL -> 0.0
        }
    }

    /** Whether two amounts are the same to the grosz. */
    fun sameCents(a: Double, b: Double): Boolean = abs(toCents(a) - toCents(b)) == 0L
}

/** SH-131: a workplace's figure for a PROPORTIONAL split, from its month's totals (the combined side). */
fun RosterFinancials.basisFor(basis: AllocationBasis): Double = when (basis) {
    AllocationBasis.NET_SALES -> combined.revenue
    AllocationBasis.INCOME -> combined.salesIncomeAmount + combined.fixedRevenue + combined.otherRevenue
    AllocationBasis.LABOUR_COST -> combined.laborCost
    AllocationBasis.EQUAL -> 1.0
}
